import { useEffect, useState } from "react";
import { Image, Spin, Empty } from "antd";
import { ReportService } from "../../../services/reports-services";
import { useNavigate } from "react-router-dom";
import useUserStore from "../../../stores/store";
import TruncatedText from "../../common/TruncatedText";

export default function ReportTable({
  paymentReceivedData = [],
  data,
  context,
  page,
  perPage,
  isLoading,
  actions = [],
  handleView = () => {},
  handleEdit = () => {},
  handleDelete = () => {},
}) {
  const { showNotification } = useUserStore();
  const [tableCols, setTableCols] = useState([]);

  const navigate = useNavigate();

  /** init report table */
  useEffect(() => {
    if (data == null) return;
    if (!Array.isArray(data) && data.length < 1) {
      return;
    }
    // set the table column names
    setTableCols(ReportService.getTableColumns(data));
  }, [data]);

  return (
    <section className="w-full mb-[44px] min-h-[50vh]">
      {isLoading && (data === null || data === undefined) ? (
        <></>
      ) : !isLoading && Array.isArray(data) && data.length === 0 ? (
        <div className="flex items-center justify-center mt-10">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE} // Keeps the standard simple icon
            description={
              <span className="font-bold text-[16px] text-gray-800 bg-yellow-100 px-3 py-1">No data found</span>
            }
          />
        </div>
      ) : (
        Array.isArray(data) &&
        data.length > 0 && (
          <div>
            {!["customers", "coupons"].includes(context) && (
              <p className="text-[14px] py-2">
                <span className="font-semibold">Note: </span> All prices are in TK
              </p>
            )}
            <table className="w-full report-table">
              <thead className="h-[56px] bg-[#EFEFEF] border border-px border-[#E9EBEF]">
                <tr>
                  {tableCols?.map((col, idx) => {
                    if (col !== "ID") {
                      return (
                        <th
                          key={idx}
                          className={`font-p_semibold text-[12px] 
                        ${ReportService.getHeaderTextColor(col)}`}
                        >
                          {col}
                        </th>
                      );
                    } else {
                      return (
                        <th
                          key={idx}
                          className={`font-p_semibold text-[12px] 
                        ${ReportService.getHeaderTextColor(col)}`}
                        ></th>
                      );
                    }
                  })}
                  {!["item-wise-sales", "customers", "orders-archived", "coupons"].includes(context) && (
                    <th
                      className={`font-p_semibold text-[12px] 
                  ${ReportService.getHeaderTextColor("Action")}`}
                    >
                      {"Action"}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="text-center">
                {data?.map((item, idx) => {
                  return (
                    <tr key={idx} className="bg-white h-[56px] border border-red-400" style={{ marginBottom: "4px" }}>
                      {/* SN */}
                      <td
                        className="border-t border-t-[#E9EBEF]
          border-b border-b-[#E9EBEF]
          border-l border-l-[#E9EBEF] align-middle"
                      >
                        {page === 1 ? idx + 1 : perPage * (page - 1) + idx + 1}
                      </td>

                      {/* Dynamic columns */}
                      {Object.entries(item)?.map(([key, value], idx) => {
                        if (key === "id") {
                          return (
                            <td
                              key={idx}
                              className="border-t border-t-[#E9EBEF]
                border-b border-b-[#E9EBEF]"
                            />
                          );
                        }

                        return (
                          <td
                            key={idx}
                            className={`text-[12px] font-p_medium leading-[18px]
              border-t border-t-[#E9EBEF]
              border-b border-b-[#E9EBEF]
              ${idx === Object.entries(item).length - 1 && actions.length < 1 && "border-r border-r-[#E9EBEF]"}
              whitespace-normal break-words break-all max-w-[200px] px-1 align-middle
              ${
                ["netTotal", "grossTotal", "soldPrice", "amount"].includes(key)
                  ? "text-[#D64B45]"
                  : key === "sold"
                    ? "text-[#2EC114]"
                    : key === "status"
                      ? ["Pending", "New"].includes(value)
                        ? "text-[#FF0A00]"
                        : "text-[#2EC114]"
                      : "text-black"
              }`}
                          >
                            {["dateTime", "createdAt", "validFrom", "validUntil"].includes(key) ? (
                              <span>{ReportService.formatDateTime(value)}</span>
                            ) : key === "orderType" ? (
                              <span className="capitalize">
                                {value === "Dine-in" ? (
                                  context === "sales" ? (
                                    "Dine-In"
                                  ) : (
                                    <Image src="/images/icons/dine_in.svg" preview={false} alt="dine-in" />
                                  )
                                ) : context === "sales" ? (
                                  value
                                ) : (
                                  <Image src="/images/icons/dine_takeout.svg" preview={false} alt="takeout" />
                                )}
                              </span>
                            ) : key === "payment" && typeof value === "object" ? (
                              <div className="w-full flex items-center justify-center">
                                <div className="text-left min-w-[85px]">
                                  {value.map((p) => (
                                    <div key={p.id} className="mt-1 leading-tight">
                                      <div className="flex items-center gap-[0.5px]">
                                        {p.paymentReceiveType === "CARD" ? (
                                          <TruncatedText
                                            text={ReportService.getPosBankLabel(
                                              paymentReceivedData,
                                              p.posBankId,
                                              p.paymentReceiveType,
                                            )}
                                            maxLength={40}
                                          />
                                        ) : (
                                          <TruncatedText text={p.paymentReceiveType} maxLength={40} />
                                        )}
                                        : <p className="break-all">{p.amount.toFixed(2)}</p>
                                      </div>

                                      {p.refNo && <div className="text-[11px] mt-[0.5px]">ref: {p.refNo}</div>}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <span>{value}</span>
                            )}
                          </td>
                        );
                      })}

                      {/* Action column */}
                      {actions.length > 0 && (
                        <td
                          className="border-t border-t-[#E9EBEF]
                      border-b border-b-[#E9EBEF]
                      border-r border-r-[#E9EBEF]
                      cursor-pointer align-middle"
                        >
                          <div className="flex items-center justify-center space-x-3 h-full">
                            {actions.includes("view") && (
                              <Image
                                height={38}
                                width={38}
                                src="/images/icons/view.svg"
                                alt="view"
                                preview={false}
                                onClick={() => handleView(item.id)}
                              />
                            )}

                            {actions.includes("edit") && (
                              <Image
                                height={38}
                                width={38}
                                src="/images/icons/item_edit.svg"
                                alt="edit"
                                preview={false}
                                onClick={() => handleEdit(item.id)}
                              />
                            )}

                            {actions.includes("delete") && (
                              <div className="bg-[#FFD700] h-[38px] w-[38px] rounded-full flex items-center justify-center">
                                <Image
                                  height={20}
                                  width={20}
                                  src="/images/icons/delete.svg"
                                  alt="delete"
                                  preview={false}
                                  onClick={() => handleDelete(item.id)}
                                />
                              </div>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>

              {/* {!["customers", "order"].includes(context) && (
            <tfoot
              className={`bg-[#FFFDEE] h-[56px] w-full text-center ${
                context == "refunds" && "relative"
              }`}
            >
              <tr className="text-[#4C00A2] font-p_semibold text-[16px] leading-[18px]">
                {tableCols?.map((colName, idx) => {
                  if (calculationFields.includes(colName)) {
                    if (context == "refunds") {
                      return (
                        <td
                          className="border-t border-t-[1px] border-t-[#E9EBEF] 
                      border-b border-b-[1px] border-b-[#E9EBEF]"
                          key={idx}
                        >
                          <div className="absolute w-full left-0 top-5 flex items-center justify-around">
                            <p>
                              Refund Order Amount -{" "}
                              {Number(refundsSummary.totalRefundAmount).toFixed(
                                2
                              )}
                            </p>
                            <p>
                              Pending Amount -{" "}
                              {Number(
                                refundsSummary.totalPendingAmount
                              ).toFixed(2)}
                            </p>
                          </div>
                        </td>
                      );
                    } else {
                      return (
                        <td
                          className="border-t border-t-[1px] border-t-[#E9EBEF] 
                      border-b border-b-[1px] border-b-[#E9EBEF]"
                          key={idx}
                        >
                          {ReportService.getColumnTotal(data, colName)} TK
                        </td>
                      );
                    }
                  } else {
                    return (
                      <td
                        className={`border-t border-t-[1px] border-t-[#E9EBEF] 
                  border-b border-b-[1px] border-b-[#E9EBEF] 
                  ${idx == 0 && "border-l border-l-[1px] border-l-[#E9EBEF]"}`}
                        key={idx}
                      >
                      </td>
                    );
                  }
                })}
                <td
                  className="border-t border-t-[1px] border-t-[#E9EBEF] 
              border-b border-b-[1px] border-b-[#E9EBEF]
              border-r border-r-[1px] border-r-[#E9EBEF]"
                ></td>
              </tr>
            </tfoot>
          )} */}
            </table>
          </div>
        )
      )}
    </section>
  );
}
